import { messages as en } from '@/locales/en/messages.po';
import { messages as es } from '@/locales/es/messages.po';
import { messages as th } from '@/locales/th/messages.po';
import { messages as zh } from '@/locales/zh/messages.po';
import {
  defaultLocale,
  localeFontConfig,
  localeFontFamilyProperty,
  localeFontStylesheetId,
  supportedLocales,
} from '@/shared/config/i18n';
import type { SupportedLocale } from '@/shared/types/i18n';
import { i18n } from '@lingui/core';
import { I18nProvider as LinguiProvider } from '@lingui/react';
import type { ReactNode } from 'react';
export type { SupportedLocale };

const LOCALE_STORAGE_KEY = 'vision-lab-locale';

// Catalogs are small, so load all of them up front and switch synchronously.
i18n.load({ en, es, th, zh });

/** Resolve an explicit saved preference; first-time visitors start in English. */
function detectUserLocale(): SupportedLocale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    return stored && (supportedLocales as readonly string[]).includes(stored)
      ? (stored as SupportedLocale)
      : defaultLocale;
  } catch {
    return defaultLocale;
  }
}

function syncDocumentLocale(locale: SupportedLocale): void {
  const font = localeFontConfig[locale];
  document.documentElement.lang = locale;
  document.documentElement.style.setProperty(localeFontFamilyProperty, `"${font.family}"`);

  let stylesheet = document.querySelector<HTMLLinkElement>(`link#${localeFontStylesheetId}`);
  if (!stylesheet) {
    stylesheet = document.createElement('link');
    stylesheet.id = localeFontStylesheetId;
    stylesheet.rel = 'stylesheet';
    document.head.append(stylesheet);
  }

  if (stylesheet.getAttribute('href') !== font.stylesheetUrl) {
    stylesheet.href = font.stylesheetUrl;
  }
}

/** Switch the active locale and its document-level language/font at runtime. */
function activateLocale(locale: SupportedLocale): void {
  syncDocumentLocale(locale);
  i18n.activate(locale);
}

/** Save and activate the locale selected by the user. */
export function setPreferredLocale(locale: SupportedLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage may be unavailable in privacy-restricted browsers; switching still works.
  }
  activateLocale(locale);
}

// Activate the detected locale before first render — no async gate, no flash.
activateLocale(detectUserLocale());

export function I18nProvider({ children }: { children: ReactNode }) {
  return <LinguiProvider i18n={i18n}>{children}</LinguiProvider>;
}
